import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Modal,
  Grid,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useLocation } from "react-router-dom";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) return decodeURIComponent(t.substring(name.length + 1));
  }
  return null;
}

const IVA_OPTIONS = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 15, label: "15%" },
  { value: 999, label: "No objeto de IVA" },
];

const ESTADO_OPTIONS = [
  { value: "activo", label: "Activo" },
  { value: "inactivo", label: "Inactivo" },
];

const AdminServices = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [services, setServices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  useEffect(() => {
    const s = location.state?.snackbar;
    if (s?.message) {
      setSnackbar({ open: true, type: s.type, message: s.message });
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const fetchServices = async () => {
    try {
      const r = await fetch("/servicios/ver-servicios/", { credentials: "include" });
      const d = await r.json();
      setServices(d.services || []);
    } catch {
      setServices([]);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    const t = search.toLowerCase();
    setFiltered(
      services.filter(
        (s) =>
          s.code.toLowerCase().includes(t) ||
          s.name.toLowerCase().includes(t)
      )
    );
    setPage(0);
  }, [search, services]);

  const handleDelete = async () => {
    try {
      const r = await fetch(`/servicios/eliminar-servicio/${deleteId}/`, {
        method: "DELETE",
        headers: { "X-CSRFToken": getCSRFToken() },
        credentials: "include",
      });

      if (r.ok) {
        setSnackbar({ open: true, type: "success", message: "Servicio eliminado correctamente" });
        fetchServices();
      } else {
        setSnackbar({ open: true, type: "error", message: "No se pudo eliminar el servicio" });
      }
    } catch {
      setSnackbar({ open: true, type: "error", message: "Error al eliminar servicio" });
    } finally {
      setDeleteId(null);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);

    try {
      const r = await fetch(`/servicios/editar-servicio/${selected.id}/`, {
        method: "POST",
        headers: { "X-CSRFToken": getCSRFToken() },
        credentials: "include",
        body: form,
      });

      if (r.ok) {
        setSnackbar({ open: true, type: "success", message: "Servicio actualizado correctamente" });
        setEditOpen(false);
        fetchServices();
      } else {
        setSnackbar({ open: true, type: "error", message: "No se pudo actualizar el servicio" });
      }
    } catch {
      setSnackbar({ open: true, type: "error", message: "Error al editar servicio" });
    }
  };

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
              onClick={() => navigate("/admin-panel/servicios/nuevo")}
            >
              Nuevo Servicio
            </Button>
          </Box>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <TextField
              fullWidth
              label="Buscar servicio"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Código</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Servicio</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>PVP</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>IVA</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Estado</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="center">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>{s.code}</TableCell>
                        <TableCell>{s.name}</TableCell>
                        <TableCell>${Number(s.pvp1).toFixed(2)}</TableCell>
                        <TableCell>${Number(s.iva_valor).toFixed(2)}</TableCell>
                        <TableCell>{s.estado === "activo" ? "Activo" : "Inactivo"}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
                              onClick={() => {
                                setSelected(s);
                                setEditOpen(true);
                              }}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="error"
                              onClick={() => setDeleteId(s.id)}
                            >
                              Eliminar
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, n) => setPage(n)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        </Box>
      </Box>

      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <Box
          component="form"
          onSubmit={handleEditSubmit}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 3,
            borderRadius: 2,
            width: 520,
          }}
        >
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField name="name" defaultValue={selected.name} fullWidth label="Nombre" />
              </Grid>
              <Grid item xs={6}>
                <TextField name="pvp1" defaultValue={selected.pvp1} fullWidth label="PVP" />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>IVA</InputLabel>
                  <Select name="iva_porcentaje" defaultValue={selected.iva_porcentaje}>
                    {IVA_OPTIONS.map((i) => (
                      <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select name="estado" defaultValue={selected.estado}>
                    {ESTADO_OPTIONS.map((e) => (
                      <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  defaultValue={selected.description}
                  fullWidth
                  multiline
                  rows={4}
                  label="Descripción"
                />
              </Grid>
              <Grid item xs={12} textAlign="right">
                <Button type="submit" variant="contained" sx={{ bgcolor: "#1565C0" }}>
                  Guardar Cambios
                </Button>
              </Grid>
            </Grid>
          )}
        </Box>
      </Modal>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 3,
            borderRadius: 2,
            width: 350,
          }}
        >
          <Typography variant="h6" mb={2}>¿Eliminar servicio?</Typography>
          <Typography mb={3}>Esta acción no se puede deshacer.</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="contained"
              sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
              onClick={() => setDeleteId(null)}
            >
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Eliminar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.type} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminServices;
